import { useContext, useEffect, useState } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";
import {
  TrendingUp,
  TrendingDown,
  Users,
  ShoppingCart,
  Package,
  DollarSign,
  ChevronDown,
} from "lucide-react";

import { useNavigate } from "react-router-dom";
import { BASE_URL } from "../../../config/api.jsx";
import axios from "axios";
import CustomerGrowthMap from "./Customergrowthmap.jsx";
import RetailerOnboardingChart from "./Retaileronboardingchart.jsx";
import ProductWarrantyChart from "./ProductWarrantyChart.jsx";
import DashboardOverviewCard from "./Dashboardoverviewcard.jsx";
import DateRangePicker from "../../../Component/Common/DateRangePicker.jsx";
import { AuthContext } from "../../../context/AuthContext.jsx";

// ─── helpers ────────────────────────────────────────────────────────────────
const fmt = (n) =>
  n >= 1_000_000
    ? `$${(n / 1_000_000).toFixed(2)}M`
    : n >= 1_000
    ? `$${(n / 1_000).toFixed(0)},${String(Math.round(n % 1000)).padStart(3, "0")}`
    : `$${n}`;

const fmtCount = (n) =>
  n >= 1_000_000
    ? `${(n / 1_000_000).toFixed(1)}M`
    : n >= 1_000
    ? `${Math.floor(n / 1000)},${String(n % 1000).padStart(3, "0")}`
    : String(n);

// ─── Sub-components ──────────────────────────────────────────────────────────

function StatCard({ icon: Icon, iconBg, label, value, change, isNeg }) {
  return (
    <div className="bg-white rounded-2xl p-5 flex items-start gap-4 shadow-sm border border-slate-100">
      <div
        className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
        style={{ background: iconBg }}
      >
        <Icon className="w-5 h-5 text-white" />
      </div>
      <div className="min-w-0">
        <p className="text-xs text-slate-400 font-medium mb-0.5">{label}</p>
        <p className="text-xl font-bold text-slate-800 leading-tight">{value}</p>
        {change && (
          <p
            className={`text-[11px] font-medium mt-0.5 flex items-center gap-0.5 ${
              isNeg ? "text-red-500" : "text-emerald-500"
            }`}
          >
            {isNeg ? (
              <TrendingDown className="w-3 h-3" />
            ) : (
              <TrendingUp className="w-3 h-3" />
            )}
            {change}
            <span className="text-slate-400 font-normal ml-1">vs last 7 days</span>
          </p>
        )}
      </div>
    </div>
  );
}

const revenueData = [
  { day: "Sun", website: 420, ecommerce: 180 },
  { day: "Mon", website: 600, ecommerce: 280 },
  { day: "Tue", website: 827, ecommerce: 390 },
  { day: "Wed", website: 750, ecommerce: 320 },
  { day: "Thu", website: 820, ecommerce: 400 },
  { day: "Fri", website: 680, ecommerce: 260 },
  { day: "Sat", website: 900, ecommerce: 450 },
];

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-slate-800 text-white text-xs rounded-lg px-3 py-2 shadow-xl">
        <p className="font-semibold mb-1">Total revenue</p>
        <p className="text-blue-300">
          ${((payload[0]?.value || 0) + (payload[1]?.value || 0)).toFixed(2)}
        </p>
      </div>
    );
  }
  return null;
};

// countryData replaced by CustomerGrowthMap component

const topTransactions = [
  { id: "#23442", item: "Leather crop-top & pant...", date: "12 Jan", amount: "$2,349" },
  { id: "#25666", item: "Female Tote Bag", date: "3 Jan", amount: "$2,047", highlight: true },
  { id: "#25666", item: "The Luxury Neckl...", date: "4 Jan", amount: "$2,047" },
  { id: "#11929", item: "s'Shoes Leather", date: "2 Jan", amount: "$1,939" },
];


// ─── Main ────────────────────────────────────────────────────────────────────
export default function OverView() {
  const { user: authUser } = useContext(AuthContext);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);

  const [onboardingData, setOnboardingData] = useState([]); 
  const [loadingOnboarding, setLoadingOnboarding] = useState(false);

  const [productData, setProductData] = useState([]);
  const [loadingProduct, setLoadingProduct] = useState(false);

  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  const [analytics, setAnalytics] = useState(null);

  const [dash, setDash] = useState({
    category_count: 0,
    brand_count: 0,
    product_count: 0,
    company_count: 0,
    agent_count: 0,
    retailer_count: 0,
    price_templates_count: 0,
    connected_retailers_count: 0,
    active_warranties_count: 0,
    open_claims_count: 0,
  });
  const [activeTab, setActiveTab] = useState("Last week");

  const [dateRange, setDateRange] = useState({
  start: null,
  end: null,
});


//   const handleTabChange = (tab) => {
//   setActiveTab(tab);

//   const now = new Date();
//   let start = new Date();

//   if (tab === "Last 24 hour") {
//     start.setDate(now.getDate() - 1);
//   } else if (tab === "Last week") {
//     start.setDate(now.getDate() - 7);
//   } else if (tab === "Last month") {
//     start.setMonth(now.getMonth() - 1);
//   } else if (tab === "Last year") {
//     start.setFullYear(now.getFullYear() - 1);
//   }

//   setDateRange({
//     start,
//     end: now,
//   });
// };


const formatDate = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
    d.getDate()
  ).padStart(2, "0")}`;

const handleTabChange = (tab) => {
  setActiveTab(tab);

  let range = "";
  const now = new Date();
  let start, end;

  // 🔥 RESET month for all tabs FIRST
  setSelectedMonth(now.getMonth());
  setSelectedYear(now.getFullYear());

  if (tab === "Last 24 hour") {
    range = "yesterday";

    const yesterday = new Date();
    yesterday.setDate(now.getDate() - 1);
    start = yesterday;
    end = now;
  }

  else if (tab === "Last week") {
    range = "last_week";

    const day = now.getDay();
    const lastMonday = new Date(now);
    lastMonday.setDate(now.getDate() - day - 6);

    const lastSunday = new Date(now);
    lastSunday.setDate(now.getDate() - day);

    start = lastMonday;
    end = lastSunday;
  }

  else if (tab === "Last month") {
    range = "last_month";

    const prev = new Date(now.getFullYear(), now.getMonth() - 1, 1);

    start = new Date(prev.getFullYear(), prev.getMonth(), 1);
    end = new Date(prev.getFullYear(), prev.getMonth() + 1, 0);

    // 🔥 override with previous month
    setSelectedMonth(prev.getMonth());
    setSelectedYear(prev.getFullYear());
  }

  // 🔥 FIX THIS ALSO (important)
  fetchAnalyticsDashboard({ range: range });

  const start_date = formatDate(start);
  const end_date = formatDate(end);

  fetchOnboarding(start_date, end_date);
  fetchProductSales(start_date, end_date);

  setDateRange({ start, end });
};

  useEffect(() => {

  const fetchDashboard = async () => {

    try {

      const response =
        await axios.get(
          `${BASE_URL}warranty/warranty-dashboard`,
          {
            headers: {
              Accept:
                "application/json",

              "Content-Type":
                "application/json",
            },
          }
        );

      const json =
        response.data;

      if (
        json?.status &&
        json?.data
      ) {

        setDash(
          json.data
        );
      }

    } catch (err) {

      console.error(
        "Dashboard fetch error:",
        err
      );

    } finally {

      setLoading(
        false
      );

    }
  };

  fetchDashboard();

}, []);

  const getMonthRange = (year, month) => {
  const start = new Date(year, month, 1);
  const end = new Date(year, month + 1, 0);

  const format = (d) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
      d.getDate()
    ).padStart(2, "0")}`;

  return {
    start_date: format(start),
    end_date: format(end),
  };
};


const getParams = () => {

  const userRole = Number(authUser.role);
  const role = authUser.role;
  const id = authUser.id;
  const companyId = authUser.company_id;

  let params = {};

  if (role == 2) {
    params.company_id = id;
  }

  if (role == 4) {
    params.company_id = companyId;
    params.agent_id = id;
  }

  return params;
};

const fetchOnboarding = async (start_date, end_date) => {
  try {
    setLoadingOnboarding(true);

    const params = {
      ...getParams(),
      start_date,
      end_date,
    };

    const res = await axios.get(
      `${BASE_URL}warranty/onboarding-stats`,
      { params }
    );

    if (res.data) {
      // expected format → convert to chart format
      const formatted = res.data.map((item) => ({
        date: item.date,
        count: item.count,
      }));

      setOnboardingData(formatted);
    }
  } catch (err) {
    console.error("Onboarding API error:", err);
  } finally {
    setLoadingOnboarding(false);
  }
};

useEffect(() => {
  const now = new Date();
  const { start_date, end_date } = getMonthRange(
    now.getFullYear(),
    now.getMonth()
  );

  fetchOnboarding(start_date, end_date);
}, []);


const fetchProductSales = async (start_date, end_date) => {
  try {
    setLoadingProduct(true);

    const params = {
      ...getParams(),
      start_date,
      end_date,
    };

    const res = await axios.get(
      `${BASE_URL}warranty/product-sales-report`,
      { params }
    );

    if (res.data) {
      const normalizeData = (data) => {
      const keys = new Set();

      data.forEach((d) => {
        Object.keys(d).forEach((k) => {
          if (k !== "date") keys.add(k);
        });
      });

      return data.map((d) => {
        const row = { date: d.date };
        keys.forEach((k) => {
          row[k] = d[k] || 0;
        });
        return row;
      });
    };
    const normalizeFullData = (data, start_date, end_date) => {
    const keys = new Set();

    data.forEach((d) => {
      Object.keys(d).forEach((k) => {
        if (k !== "date") keys.add(k);
      });
    });

    const start = new Date(start_date);
    const end = new Date(end_date);

    const result = [];

    for (
      let d = new Date(start);
      d <= end;
      d.setDate(d.getDate() + 1)
    ) {
      const dateStr = d.toISOString().split("T")[0];

      const found = data.find((item) => item.date === dateStr);

      const row = { date: dateStr };

      keys.forEach((k) => {
        row[k] = found ? found[k] || 0 : 0;
      });

      result.push(row);
    }

    return result;
  };

    setProductData(normalizeFullData(res.data, start_date, end_date));
    }
  } catch (err) {
    console.error("Product API error:", err);
  } finally {
    setLoadingProduct(false);
  }
};

 const fetchAnalyticsDashboard = async (extraParams = {}) => {
  try {
    const role = authUser.role;
    const id = authUser.id;
    const companyId = authUser.company_id;

    let params = {
      ...extraParams, // 🔥 include type / date filters
    };

    if (role == 2) {
      params.company_id = id;
    }

    if (role == 4) {
      params.company_id = companyId;
      params.agent_id = id;
    }

    const res = await axios.get(
      `${BASE_URL}warranty/analytics/dashboard`,
      { params }
    );

    if (res.data?.status) {
      setAnalytics(res.data.data);
    }
  } catch (err) {
    console.error("Analytics API error:", err);
  }
};

useEffect(() => {
  fetchAnalyticsDashboard(); // default
}, []);


useEffect(() => {
  const now = new Date();
  const { start_date, end_date } = getMonthRange(
    now.getFullYear(),
    now.getMonth()
  );

  fetchProductSales(start_date, end_date);
}, []);

  const tabs = ["Last 24 hour", "Last week", "Last month"];

  const filteredOnboardingData = onboardingData.filter((item) => {
  if (!dateRange.start || !dateRange.end) return true;

  const d = new Date(item.date);
  return d >= dateRange.start && d <= dateRange.end;
});

// const filteredProductData = productWarrantyData.filter((item) => {
//   if (!dateRange.start || !dateRange.end) return true;

//   const d = new Date(item.date);
//   return d >= dateRange.start && d <= dateRange.end;
// });

const handleMonthChange = (monthIndex, year) => {
  const { start_date, end_date } = getMonthRange(
    parseInt(year),
    monthIndex
  );

  fetchOnboarding(start_date, end_date);
  fetchProductSales(start_date, end_date); // 🔥 ADD THIS
};

// 🔥 SUMMARY CALCULATIONS

const totalOnboarding = onboardingData.reduce(
  (sum, item) => sum + item.count,
  0
);

const totalProductSales = productData.reduce((sum, row) => {
  return (
    sum +
    Object.keys(row)
      .filter((k) => k !== "date")
      .reduce((s, k) => s + row[k], 0)
  );
}, 0);

// find top product
const productTotals = {};

productData.forEach((row) => {
  Object.keys(row).forEach((k) => {
    if (k !== "date") {
      productTotals[k] = (productTotals[k] || 0) + row[k];
    }
  });
});

const topProduct =
  Object.entries(productTotals).sort((a, b) => b[1] - a[1])[0]?.[0] || "-";

const handleDateChange = (start, end) => {
  setDateRange({ start, end });

  if (start && end) {
    const start_date = formatDate(start);
    const end_date = formatDate(end);

    // 🔥 call analytics with date range
    fetchAnalyticsDashboard({ start_date, end_date });

    fetchOnboarding(start_date, end_date);
    fetchProductSales(start_date, end_date);

    // optional: remove tab highlight
    setActiveTab("");
  }

  if (!start && !end) {
    const now = new Date();
    const { start_date, end_date } = getMonthRange(
      now.getFullYear(),
      now.getMonth()
    );

    fetchAnalyticsDashboard({}); // default

    fetchOnboarding(start_date, end_date);
    fetchProductSales(start_date, end_date);

    setActiveTab("Last week");
  }
};

// 🔥 dynamic values based on tab
let warrantySold = 0;

if (activeTab === "Last 24 hour") {
  warrantySold = analytics?.yesterday?.sales || 0;
} else if (activeTab === "Last week") {
  warrantySold = analytics?.custom_range?.sales || 0;
} else if (activeTab === "Last month") {
  warrantySold = analytics?.custom_range?.sales || 0;
}

// 🔥 onboarding (retailers)
const retailerOnboard = analytics?.retailers?.total || 0;

// 🔥 top product
const topProductName = analytics?.top_product?.product_name || "-";

// 🔥 growth
const onboardingChange = `${analytics?.growth?.today_vs_yesterday || 0}%`;
const productChange = `${analytics?.growth?.week_vs_last_week || 0}%`;
const customerChange = `${analytics?.retailers?.growth || 0}%`;

  return (
    <div
      className="min-h-screen font-sans"
      style={{ fontFamily: "'DM Sans', 'Segoe UI', sans-serif" }}
    >
      {/* ── Header ── */}
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-2">
          {tabs.map((t) => (
            <button

              key={t}
              onClick={() => handleTabChange(t)}
              className={`text-sm px-3 py-1.5 rounded-full font-medium transition-all ${
                activeTab === t
                  ? "text-blue-600 border-b-2 border-blue-600 bg-transparent rounded-none px-0 mr-2"
                  : "text-slate-400 hover:text-slate-600"
              }`}
            >
              {t}
            </button>
          ))}
        </div>
       <DateRangePicker
        startDate={dateRange.start}
        endDate={dateRange.end}
        onChange={handleDateChange}
      />
      </div>

      {/* ── Charts Row ── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">

        {/* <div
          className="lg:col-span-1 rounded-2xl p-6 text-white relative overflow-hidden"
          style={{
            background:
              "linear-gradient(135deg, #1e3a5f 0%, #2563eb 60%, #7c3aed 100%)",
          }}
        >

          <div className="absolute -top-8 -right-8 w-36 h-36 rounded-full bg-white opacity-5" />
          <div className="absolute bottom-4 -left-6 w-24 h-24 rounded-full bg-white opacity-5" />


          <h3 className="text-lg font-bold mb-4">Dashboard Overview</h3>


          <div className="space-y-3 text-sm">

            <div className="flex justify-between">
              <span className="text-blue-100">Total Onboarded</span>
              <span className="font-semibold">{totalOnboarding}</span>
            </div>

            <div className="flex justify-between">
              <span className="text-blue-100">Total Product Sales</span>
              <span className="font-semibold">{totalProductSales}</span>
            </div>

            <div className="flex justify-between">
              <span className="text-blue-100">Top Product</span>
              <span className="font-semibold truncate max-w-[140px]">
                {topProduct}
              </span>
            </div>

          </div>

          <button className="mt-5 bg-white text-blue-700 text-xs font-semibold px-4 py-2 rounded-lg hover:bg-blue-50 transition shadow">
            View Details
          </button>
        </div> */}

        <DashboardOverviewCard
  totalOnboarding={retailerOnboard}     // retailers total
  totalProductSales={warrantySold}      // based on tab
  customerCount={retailerOnboard}       // same as retailers

  onboardingChange={onboardingChange}
  customerChange={customerChange}
  productChange={productChange}
  topProductName={topProductName}
/>

        {/* Revenue Growth */}
        <RetailerOnboardingChart 
  data={onboardingData} 
  onMonthChange={handleMonthChange}
  selectedMonth={selectedMonth}
  selectedYear={selectedYear}
/>

      </div>

      {/* ── Bottom Row ── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">

        {/* Customer Growth map */}
        <CustomerGrowthMap dateRange={dateRange} />

        <ProductWarrantyChart data={productData} />

      </div>
    </div>
  );
}

// import { useEffect, useState } from "react";
// import {
//   BarChart,
//   Bar,
//   XAxis,
//   YAxis,
//   Tooltip,
//   ResponsiveContainer,
//   CartesianGrid,
// } from "recharts";
// import {
//   TrendingUp,
//   TrendingDown,
//   Users,
//   ShoppingCart,
//   Package,
//   DollarSign,
//   ChevronDown,
// } from "lucide-react";

// import { useNavigate } from "react-router-dom";
// import { BASE_URL } from "../../../config/api.jsx";
// import axios from "axios";
// import CustomerGrowthMap from "./Customergrowthmap.jsx";
// import RetailerOnboardingChart from "./Retaileronboardingchart.jsx";
// import ProductWarrantyChart from "./ProductWarrantyChart.jsx";

// // ─── helpers ────────────────────────────────────────────────────────────────
// const fmt = (n) =>
//   n >= 1_000_000
//     ? `$${(n / 1_000_000).toFixed(2)}M`
//     : n >= 1_000
//     ? `$${(n / 1_000).toFixed(0)},${String(Math.round(n % 1000)).padStart(3, "0")}`
//     : `$${n}`;

// const fmtCount = (n) =>
//   n >= 1_000_000
//     ? `${(n / 1_000_000).toFixed(1)}M`
//     : n >= 1_000
//     ? `${Math.floor(n / 1000)},${String(n % 1000).padStart(3, "0")}`
//     : String(n);

// // ─── Sub-components ──────────────────────────────────────────────────────────

// function StatCard({ icon: Icon, iconBg, label, value, change, isNeg }) {
//   return (
//     <div className="bg-white rounded-2xl p-5 flex items-start gap-4 shadow-sm border border-slate-100">
//       <div
//         className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
//         style={{ background: iconBg }}
//       >
//         <Icon className="w-5 h-5 text-white" />
//       </div>
//       <div className="min-w-0">
//         <p className="text-xs text-slate-400 font-medium mb-0.5">{label}</p>
//         <p className="text-xl font-bold text-slate-800 leading-tight">{value}</p>
//         {change && (
//           <p
//             className={`text-[11px] font-medium mt-0.5 flex items-center gap-0.5 ${
//               isNeg ? "text-red-500" : "text-emerald-500"
//             }`}
//           >
//             {isNeg ? (
//               <TrendingDown className="w-3 h-3" />
//             ) : (
//               <TrendingUp className="w-3 h-3" />
//             )}
//             {change}
//             <span className="text-slate-400 font-normal ml-1">vs last 7 days</span>
//           </p>
//         )}
//       </div>
//     </div>
//   );
// }

// const revenueData = [
//   { day: "Sun", website: 420, ecommerce: 180 },
//   { day: "Mon", website: 600, ecommerce: 280 },
//   { day: "Tue", website: 827, ecommerce: 390 },
//   { day: "Wed", website: 750, ecommerce: 320 },
//   { day: "Thu", website: 820, ecommerce: 400 },
//   { day: "Fri", website: 680, ecommerce: 260 },
//   { day: "Sat", website: 900, ecommerce: 450 },
// ];

// const CustomTooltip = ({ active, payload, label }) => {
//   if (active && payload && payload.length) {
//     return (
//       <div className="bg-slate-800 text-white text-xs rounded-lg px-3 py-2 shadow-xl">
//         <p className="font-semibold mb-1">Total revenue</p>
//         <p className="text-blue-300">
//           ${((payload[0]?.value || 0) + (payload[1]?.value || 0)).toFixed(2)}
//         </p>
//       </div>
//     );
//   }
//   return null;
// };

// // countryData replaced by CustomerGrowthMap component

// const topTransactions = [
//   { id: "#23442", item: "Leather crop-top & pant...", date: "12 Jan", amount: "$2,349" },
//   { id: "#25666", item: "Female Tote Bag", date: "3 Jan", amount: "$2,047", highlight: true },
//   { id: "#25666", item: "The Luxury Neckl...", date: "4 Jan", amount: "$2,047" },
//   { id: "#11929", item: "s'Shoes Leather", date: "2 Jan", amount: "$1,939" },
// ];


// // const dummyOnboardingData = [
// //   { date: "2026-04-01", count: 5 },
// //   { date: "2026-04-02", count: 8 },
// //   { date: "2026-04-03", count: 3 },
// //   { date: "2026-04-04", count: 10 },
// //   { date: "2026-04-05", count: 6 },
// //   { date: "2026-04-06", count: 2 },
// //   { date: "2026-04-07", count: 9 },
// //   { date: "2026-04-08", count: 4 },
// //   { date: "2026-04-09", count: 7 },
// //   { date: "2026-04-10", count: 12 },
// //   { date: "2026-04-11", count: 6 },
// //   { date: "2026-04-12", count: 3 },
// //   { date: "2026-04-13", count: 8 },
// //   { date: "2026-04-14", count: 11 },
// //   { date: "2026-04-15", count: 5 },
// //   { date: "2026-04-16", count: 9 },
// //   { date: "2026-04-17", count: 4 },
// //   { date: "2026-04-18", count: 7 },
// //   { date: "2026-04-19", count: 6 },
// //   { date: "2026-04-20", count: 10 },
// // ];
// // WorldMapSVG removed — using real Leaflet map via CustomerGrowthMap

// // const productWarrantyData = [
// //   { date: "2026-04-01", mobile: 10, laptop: 6, tablet: 4 },
// //   { date: "2026-04-02", mobile: 15, laptop: 8, tablet: 5 },
// //   { date: "2026-04-03", mobile: 8,  laptop: 4, tablet: 3 },
// //   { date: "2026-04-04", mobile: 20, laptop: 10, tablet: 6 },
// //   { date: "2026-04-05", mobile: 12, laptop: 7, tablet: 5 },
// //   { date: "2026-04-06", mobile: 6,  laptop: 3, tablet: 2 },
// //   { date: "2026-04-07", mobile: 18, laptop: 9, tablet: 6 },
// // ];

// // ─── Main ────────────────────────────────────────────────────────────────────
// export default function OverView() {
//   const navigate = useNavigate();
//   const [loading, setLoading] = useState(true);

//   const [onboardingData, setOnboardingData] = useState([]); 
//   const [loadingOnboarding, setLoadingOnboarding] = useState(false);

//   const [productData, setProductData] = useState([]);
//   const [loadingProduct, setLoadingProduct] = useState(false);

//   const [dash, setDash] = useState({
//     category_count: 0,
//     brand_count: 0,
//     product_count: 0,
//     company_count: 0,
//     agent_count: 0,
//     retailer_count: 0,
//     price_templates_count: 0,
//     connected_retailers_count: 0,
//     active_warranties_count: 0,
//     open_claims_count: 0,
//   });
//   const [activeTab, setActiveTab] = useState("Last week");

//   const [dateRange, setDateRange] = useState({
//     start: null,
//     end: null,
//   });


//   const handleTabChange = (tab) => {
//   setActiveTab(tab);

//   const now = new Date();
//   let start = new Date();

//   if (tab === "Last 24 hour") {
//     start.setDate(now.getDate() - 1);
//   } else if (tab === "Last week") {
//     start.setDate(now.getDate() - 7);
//   } else if (tab === "Last month") {
//     start.setMonth(now.getMonth() - 1);
//   } else if (tab === "Last year") {
//     start.setFullYear(now.getFullYear() - 1);
//   }

//   setDateRange({
//     start,
//     end: now,
//   });
// };

//   useEffect(() => {
//     const fetchDashboard = async () => {
//       try {
//         const res = await fetch(`${BASE_URL}warranty/warranty-dashboard`);
//         const json = await res.json();
//         if (json.status && json.data) setDash(json.data);
//       } catch (err) {
//         console.error("Dashboard fetch error:", err);
//       } finally {
//         setLoading(false);
//       }
//     };
//     fetchDashboard();
//   }, []);

//   const getMonthRange = (year, month) => {
//   const start = new Date(year, month, 1);
//   const end = new Date(year, month + 1, 0);

//   const format = (d) =>
//     `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
//       d.getDate()
//     ).padStart(2, "0")}`;

//   return {
//     start_date: format(start),
//     end_date: format(end),
//   };
// };


// const getParams = () => {
//   const role = localStorage.getItem("userRole");
//   const id = localStorage.getItem("id");
//   const companyId = localStorage.getItem("company_id");

//   let params = {};

//   if (role == 2) {
//     params.company_id = id;
//   }

//   if (role == 4) {
//     params.company_id = companyId;
//     params.agent_id = id;
//   }

//   return params;
// };

// const fetchOnboarding = async (start_date, end_date) => {
//   try {
//     setLoadingOnboarding(true);

//     const params = {
//       ...getParams(),
//       start_date,
//       end_date,
//     };

//     const res = await axios.get(
//       `${BASE_URL}warranty/onboarding-stats`,
//       { params }
//     );

//     if (res.data) {
//       // expected format → convert to chart format
//       const formatted = res.data.map((item) => ({
//         date: item.date,
//         count: item.count,
//       }));

//       setOnboardingData(formatted);
//     }
//   } catch (err) {
//     console.error("Onboarding API error:", err);
//   } finally {
//     setLoadingOnboarding(false);
//   }
// };

// useEffect(() => {
//   const now = new Date();
//   const { start_date, end_date } = getMonthRange(
//     now.getFullYear(),
//     now.getMonth()
//   );

//   fetchOnboarding(start_date, end_date);
// }, []);


// const fetchProductSales = async (start_date, end_date) => {
//   try {
//     setLoadingProduct(true);

//     const params = {
//       ...getParams(),
//       start_date,
//       end_date,
//     };

//     const res = await axios.get(
//       `${BASE_URL}warranty/product-sales-report`,
//       { params }
//     );

//     if (res.data) {
//       const normalizeData = (data) => {
//       const keys = new Set();

//       data.forEach((d) => {
//         Object.keys(d).forEach((k) => {
//           if (k !== "date") keys.add(k);
//         });
//       });

//       return data.map((d) => {
//         const row = { date: d.date };
//         keys.forEach((k) => {
//           row[k] = d[k] || 0;
//         });
//         return row;
//       });
//     };

//     setProductData(normalizeData(res.data));
//     }
//   } catch (err) {
//     console.error("Product API error:", err);
//   } finally {
//     setLoadingProduct(false);
//   }
// };

// useEffect(() => {
//   const now = new Date();
//   const { start_date, end_date } = getMonthRange(
//     now.getFullYear(),
//     now.getMonth()
//   );

//   fetchProductSales(start_date, end_date);
// }, []);

//   const tabs = ["Last 24 hour", "Last week", "Last month"];

//   const filteredOnboardingData = onboardingData.filter((item) => {
//   if (!dateRange.start || !dateRange.end) return true;

//   const d = new Date(item.date);
//   return d >= dateRange.start && d <= dateRange.end;
// });

// // const filteredProductData = productWarrantyData.filter((item) => {
// //   if (!dateRange.start || !dateRange.end) return true;

// //   const d = new Date(item.date);
// //   return d >= dateRange.start && d <= dateRange.end;
// // });

// const handleMonthChange = (monthIndex, year) => {
//   const { start_date, end_date } = getMonthRange(
//     parseInt(year),
//     monthIndex
//   );

//   fetchOnboarding(start_date, end_date);
//   fetchProductSales(start_date, end_date); // 🔥 ADD THIS
// };
//   return (
//     <div
//       className="min-h-screen bg-slate-50 p-6 font-sans"
//       style={{ fontFamily: "'DM Sans', 'Segoe UI', sans-serif" }}
//     >
//       {/* ── Header ── */}
//       <div className="flex items-center justify-between mb-5">
//         <div className="flex items-center gap-2">
//           {tabs.map((t) => (
//             <button

//               key={t}
//               onClick={() => handleTabChange(t)}
//               className={`text-sm px-3 py-1.5 rounded-full font-medium transition-all ${
//                 activeTab === t
//                   ? "text-blue-600 border-b-2 border-blue-600 bg-transparent rounded-none px-0 mr-2"
//                   : "text-slate-400 hover:text-slate-600"
//               }`}
//             >
//               {t}
//             </button>
//           ))}
//         </div>
//         <button className="flex items-center gap-1.5 text-sm text-slate-600 border border-slate-200 rounded-lg px-3 py-1.5 hover:bg-white transition bg-white shadow-sm">
//           Filter by date range
//           <ChevronDown className="w-4 h-4" />
//         </button>
//       </div>

//       {/* ── KPI Row ── */}
//       {/* <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
//         <StatCard
//           icon={DollarSign}
//           iconBg="linear-gradient(135deg,#f97316,#fb923c)"
//           label="Total Revenue"
//           value="$612,839"
//           change="↑ 16%"
//           isNeg={false}
//         />
//         <StatCard
//           icon={Users}
//           iconBg="linear-gradient(135deg,#6366f1,#818cf8)"
//           label="Total Customer"
//           value="513,456"
//           change="↓ 0.4%"
//           isNeg={true}
//         />
//         <StatCard
//           icon={ShoppingCart}
//           iconBg="linear-gradient(135deg,#10b981,#34d399)"
//           label="Total Transaction"
//           value="637,902"
//           change="↑ 8%"
//           isNeg={false}
//         />
//         <StatCard
//           icon={Package}
//           iconBg="linear-gradient(135deg,#3b82f6,#60a5fa)"
//           label="Total Product"
//           value={loading ? "—" : fmtCount(dash.product_count || 256600)}
//           change="↑ 2%"
//           isNeg={false}
//         />
//       </div> */}

//       {/* ── Charts Row ── */}
//       <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
//         {/* Revenue Growth */}
//         <RetailerOnboardingChart data={onboardingData} onMonthChange={handleMonthChange}/>

//         {/* Customer Growth map */}
//         <CustomerGrowthMap dateRange={dateRange} />
//       </div>

//       {/* ── Bottom Row ── */}
//       <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
//         {/* Top Transactions */}

//         <ProductWarrantyChart data={productData} />

//         <div className="lg:col-span-1 bg-white rounded-2xl p-5 shadow-sm border border-slate-100">
//           <div className="flex items-center justify-between mb-1">
//             <div>
//               <h3 className="text-sm font-semibold text-slate-800">Top Transaction</h3>
//               <p className="text-[11px] text-slate-400">of the week based on total purchase</p>
//             </div>
//             <button className="text-xs text-blue-500 font-medium hover:underline">
//               View detail
//             </button>
//           </div>
//           <table className="w-full mt-3 text-xs">
//             <thead>
//               <tr className="text-slate-400 border-b border-slate-100">
//                 <th className="text-left pb-2 font-medium">Customer ID</th>
//                 <th className="text-left pb-2 font-medium">First Item</th>
//                 <th className="text-left pb-2 font-medium">Date</th>
//                 <th className="text-right pb-2 font-medium">Purchase</th>
//               </tr>
//             </thead>
//             <tbody>
//               {topTransactions.map((tx, i) => (
//                 <tr
//                   key={i}
//                   className={`border-b border-slate-50 last:border-0 ${
//                     tx.highlight ? "bg-blue-600 rounded-xl" : ""
//                   }`}
//                 >
//                   <td
//                     className={`py-2 font-medium ${
//                       tx.highlight ? "text-white" : "text-slate-700"
//                     }`}
//                   >
//                     {tx.id}
//                   </td>
//                   <td
//                     className={`py-2 max-w-[100px] truncate ${
//                       tx.highlight ? "text-blue-100" : "text-slate-600"
//                     }`}
//                   >
//                     {tx.item}
//                   </td>
//                   <td className={`py-2 ${tx.highlight ? "text-blue-200" : "text-slate-400"}`}>
//                     {tx.date}
//                   </td>
//                   <td
//                     className={`py-2 text-right font-semibold ${
//                       tx.highlight ? "text-white" : "text-slate-700"
//                     }`}
//                   >
//                     {tx.highlight ? (
//                       <span className="bg-white text-blue-600 rounded-full px-2 py-0.5 text-[10px]">
//                         See detail
//                       </span>
//                     ) : (
//                       tx.amount
//                     )}
//                   </td>
//                 </tr>
//               ))}
//             </tbody>
//           </table>
//         </div>

       
//       </div>

//       <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">

//          {/* Top Products */}
//         <div className="lg:col-span-1 bg-white rounded-2xl p-5 shadow-sm border border-slate-100">
//           <div className="flex items-center justify-between mb-3">
//             <div>
//               <h3 className="text-sm font-semibold text-slate-800">Top Product</h3>
//               <p className="text-[11px] text-slate-400">top 3 of the week based on total sold</p>
//             </div>
//             <button className="text-xs text-blue-500 font-medium hover:underline">
//               View more
//             </button>
//           </div>
//           <div className="grid grid-cols-3 gap-2 mb-3">
//             {[
//               { bg: "#f3f4f6", img: null, label: "Shirt" },
//               { bg: "#e0e7ff", img: null, label: "Dress" },
//               { bg: "#fef3c7", img: null, label: "Bag" },
//             ].map((p, i) => (
//               <div
//                 key={i}
//                 className="rounded-xl overflow-hidden aspect-square flex items-center justify-center text-3xl"
//                 style={{ background: p.bg }}
//               >
//                 {["👕", "👗", "👜"][i]}
//               </div>
//             ))}
//           </div>
//           <div className="border-t border-slate-100 pt-3">
//             <p className="text-sm font-semibold text-slate-800">Denim Jacket with White Feathers</p>
//             <p className="text-xs text-slate-400 mt-0.5">240+ items sold</p>
//           </div>
//         </div>

//         {/* CTA Card */}
//         <div
//           className="lg:col-span-1 rounded-2xl p-6 flex flex-col justify-between text-white relative overflow-hidden"
//           style={{
//             background: "linear-gradient(135deg, #1e3a5f 0%, #2563eb 60%, #7c3aed 100%)",
//           }}
//         >
//           {/* decorative circles */}
//           <div className="absolute -top-8 -right-8 w-36 h-36 rounded-full bg-white opacity-5" />
//           <div className="absolute bottom-4 -left-6 w-24 h-24 rounded-full bg-white opacity-5" />
//           <div className="absolute top-12 right-8 w-16 h-16 rounded-full bg-blue-400 opacity-20" />

//           <div>
//             <h3 className="text-lg font-bold leading-snug">
//               See more detail statistic to analyze your decision
//             </h3>
//           </div>
//           <button className="mt-6 self-start bg-white text-blue-700 text-sm font-semibold px-5 py-2 rounded-xl hover:bg-blue-50 transition shadow-md">
//             See more
//           </button>
//         </div>
//         </div>
//     </div>
//   );
// }

// import { useEffect, useState } from "react";
// import {
//   BarChart,
//   Bar,
//   XAxis,
//   YAxis,
//   Tooltip,
//   ResponsiveContainer,
//   CartesianGrid,
// } from "recharts";
// import {
//   TrendingUp,
//   TrendingDown,
//   Users,
//   ShoppingCart,
//   Package,
//   DollarSign,
//   ZoomIn,
//   ZoomOut,
//   ChevronDown,
// } from "lucide-react";
// import { useNavigate } from "react-router-dom";
// import { BASE_URL } from "../../../config/api.jsx";
// import axios from "axios";

// // ─── helpers ────────────────────────────────────────────────────────────────
// const fmt = (n) =>
//   n >= 1_000_000
//     ? `$${(n / 1_000_000).toFixed(2)}M`
//     : n >= 1_000
//     ? `$${(n / 1_000).toFixed(0)},${String(Math.round(n % 1000)).padStart(3, "0")}`
//     : `$${n}`;

// const fmtCount = (n) =>
//   n >= 1_000_000
//     ? `${(n / 1_000_000).toFixed(1)}M`
//     : n >= 1_000
//     ? `${Math.floor(n / 1000)},${String(n % 1000).padStart(3, "0")}`
//     : String(n);

// // ─── Sub-components ──────────────────────────────────────────────────────────

// function StatCard({ icon: Icon, iconBg, label, value, change, isNeg }) {
//   return (
//     <div className="bg-white rounded-2xl p-5 flex items-start gap-4 shadow-sm border border-slate-100">
//       <div
//         className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
//         style={{ background: iconBg }}
//       >
//         <Icon className="w-5 h-5 text-white" />
//       </div>
//       <div className="min-w-0">
//         <p className="text-xs text-slate-400 font-medium mb-0.5">{label}</p>
//         <p className="text-xl font-bold text-slate-800 leading-tight">{value}</p>
//         {change && (
//           <p
//             className={`text-[11px] font-medium mt-0.5 flex items-center gap-0.5 ${
//               isNeg ? "text-red-500" : "text-emerald-500"
//             }`}
//           >
//             {isNeg ? (
//               <TrendingDown className="w-3 h-3" />
//             ) : (
//               <TrendingUp className="w-3 h-3" />
//             )}
//             {change}
//             <span className="text-slate-400 font-normal ml-1">vs last 7 days</span>
//           </p>
//         )}
//       </div>
//     </div>
//   );
// }

// const revenueData = [
//   { day: "Sun", website: 420, ecommerce: 180 },
//   { day: "Mon", website: 600, ecommerce: 280 },
//   { day: "Tue", website: 827, ecommerce: 390 },
//   { day: "Wed", website: 750, ecommerce: 320 },
//   { day: "Thu", website: 820, ecommerce: 400 },
//   { day: "Fri", website: 680, ecommerce: 260 },
//   { day: "Sat", website: 900, ecommerce: 450 },
// ];

// const CustomTooltip = ({ active, payload, label }) => {
//   if (active && payload && payload.length) {
//     return (
//       <div className="bg-slate-800 text-white text-xs rounded-lg px-3 py-2 shadow-xl">
//         <p className="font-semibold mb-1">Total revenue</p>
//         <p className="text-blue-300">
//           ${((payload[0]?.value || 0) + (payload[1]?.value || 0)).toFixed(2)}
//         </p>
//       </div>
//     );
//   }
//   return null;
// };

// const countryData = [
//   { name: "United States", flag: "🇺🇸", pct: 38, color: "#3b82f6" },
//   { name: "United Kingdom", flag: "🇬🇧", pct: 27, color: "#3b82f6" },
//   { name: "France", flag: "🇫🇷", pct: 20, color: "#3b82f6" },
//   { name: "Argentina", flag: "🇦🇷", pct: 10, color: "#3b82f6" },
// ];

// const topTransactions = [
//   { id: "#23442", item: "Leather crop-top & pant...", date: "12 Jan", amount: "$2,349" },
//   { id: "#25666", item: "Female Tote Bag", date: "3 Jan", amount: "$2,047", highlight: true },
//   { id: "#25666", item: "The Luxury Neckl...", date: "4 Jan", amount: "$2,047" },
//   { id: "#11929", item: "s'Shoes Leather", date: "2 Jan", amount: "$1,939" },
// ];

// // Simplified SVG world map blobs (decorative)
// function WorldMapSVG() {
//   return (
//     <svg viewBox="0 0 600 300" className="w-full h-full opacity-20" fill="none">
//       {/* North America */}
//       <ellipse cx="130" cy="110" rx="80" ry="60" fill="#3b82f6" />
//       {/* South America */}
//       <ellipse cx="180" cy="200" rx="40" ry="55" fill="#3b82f6" />
//       {/* Europe */}
//       <ellipse cx="290" cy="100" rx="45" ry="40" fill="#3b82f6" />
//       {/* Africa */}
//       <ellipse cx="300" cy="190" rx="40" ry="55" fill="#3b82f6" />
//       {/* Asia */}
//       <ellipse cx="430" cy="110" rx="100" ry="65" fill="#3b82f6" />
//       {/* Australia */}
//       <ellipse cx="490" cy="210" rx="45" ry="30" fill="#3b82f6" />
//     </svg>
//   );
// }

// // ─── Main ────────────────────────────────────────────────────────────────────
// export default function OverView() {
//   const navigate = useNavigate();
//   const [loading, setLoading] = useState(true);
//   const [dash, setDash] = useState({
//     category_count: 0,
//     brand_count: 0,
//     product_count: 0,
//     company_count: 0,
//     agent_count: 0,
//     retailer_count: 0,
//     price_templates_count: 0,
//     connected_retailers_count: 0,
//     active_warranties_count: 0,
//     open_claims_count: 0,
//   });
//   const [activeTab, setActiveTab] = useState("Last week");

//   useEffect(() => {
//     const fetchDashboard = async () => {
//       try {
//         const res = await fetch(`${BASE_URL}warranty/warranty-dashboard`);
//         const json = await res.json();
//         if (json.status && json.data) setDash(json.data);
//       } catch (err) {
//         console.error("Dashboard fetch error:", err);
//       } finally {
//         setLoading(false);
//       }
//     };
//     fetchDashboard();
//   }, []);

//   const tabs = ["Last 24 hour", "Last week", "Last month", "Last year"];

//   return (
//     <div
//       className="min-h-screen  font-sans"
//       style={{ fontFamily: "'DM Sans', 'Segoe UI', sans-serif" }}
//     >
//       {/* ── Header ── */}
//       <div className="flex items-center justify-between mb-5">
//         <div className="flex items-center gap-2">
//           {tabs.map((t) => (
//             <button
//               key={t}
//               onClick={() => setActiveTab(t)}
//               className={`text-sm px-3 py-1.5 rounded-full font-medium transition-all ${
//                 activeTab === t
//                   ? "text-blue-600 border-b-2 border-blue-600 bg-transparent rounded-none px-0 mr-2"
//                   : "text-slate-400 hover:text-slate-600"
//               }`}
//             >
//               {t}
//             </button>
//           ))}
//         </div>
//         <button className="flex items-center gap-1.5 text-sm text-slate-600 border border-slate-200 rounded-lg px-3 py-1.5 hover:bg-white transition bg-white shadow-sm">
//           Filter by date range
//           <ChevronDown className="w-4 h-4" />
//         </button>
//       </div>

//       {/* ── KPI Row ── */}
//       {/* <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
//         <StatCard
//           icon={DollarSign}
//           iconBg="linear-gradient(135deg,#f97316,#fb923c)"
//           label="Total Revenue"
//           value="$612,839"
//           change="↑ 16%"
//           isNeg={false}
//         />
//         <StatCard
//           icon={Users}
//           iconBg="linear-gradient(135deg,#6366f1,#818cf8)"
//           label="Total Customer"
//           value="513,456"
//           change="↓ 0.4%"
//           isNeg={true}
//         />
//         <StatCard
//           icon={ShoppingCart}
//           iconBg="linear-gradient(135deg,#10b981,#34d399)"
//           label="Total Transaction"
//           value="637,902"
//           change="↑ 8%"
//           isNeg={false}
//         />
//         <StatCard
//           icon={Package}
//           iconBg="linear-gradient(135deg,#3b82f6,#60a5fa)"
//           label="Total Product"
//           value={loading ? "—" : fmtCount(dash.product_count || 256600)}
//           change="↑ 2%"
//           isNeg={false}
//         />
//       </div> */}

//       {/* ── Charts Row ── */}
//       <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
//         {/* Revenue Growth */}
//         <div className="bg-white rounded-2xl p-5 shadow-sm border border-slate-100">
//           <div className="flex items-center justify-between mb-1">
//             <div>
//               <h3 className="text-sm font-semibold text-slate-800">Revenue Growth (USD)</h3>
//               <p className="text-[11px] text-slate-400">
//                 of the week on website and compared with e-commerce
//               </p>
//             </div>
//             <button className="text-xs text-blue-500 font-medium hover:underline">
//               View detail
//             </button>
//           </div>
//           <div className="mt-3" style={{ height: 200 }}>
//             <ResponsiveContainer width="100%" height="100%">
//               <BarChart data={revenueData} barSize={14} barGap={3}>
//                 <CartesianGrid vertical={false} stroke="#f1f5f9" />
//                 <XAxis
//                   dataKey="day"
//                   axisLine={false}
//                   tickLine={false}
//                   tick={{ fontSize: 11, fill: "#94a3b8" }}
//                 />
//                 <YAxis
//                   axisLine={false}
//                   tickLine={false}
//                   tick={{ fontSize: 11, fill: "#94a3b8" }}
//                 />
//                 <Tooltip content={<CustomTooltip />} cursor={{ fill: "#f8fafc" }} />
//                 <Bar dataKey="website" fill="#3b82f6" radius={[4, 4, 0, 0]} />
//                 <Bar dataKey="ecommerce" fill="#bfdbfe" radius={[4, 4, 0, 0]} />
//               </BarChart>
//             </ResponsiveContainer>
//           </div>
//           <div className="flex gap-4 mt-2">
//             <span className="flex items-center gap-1.5 text-[11px] text-slate-500">
//               <span className="w-3 h-3 rounded-sm bg-blue-500 inline-block" /> Website
//             </span>
//             <span className="flex items-center gap-1.5 text-[11px] text-slate-500">
//               <span className="w-3 h-3 rounded-sm bg-blue-200 inline-block" /> E-commerce
//             </span>
//           </div>
//         </div>

//         {/* Customer Growth map */}
//         <div className="bg-white rounded-2xl p-5 shadow-sm border border-slate-100">
//           <div className="flex items-center justify-between mb-3">
//             <div>
//               <h3 className="text-sm font-semibold text-slate-800">Customer Growth</h3>
//               <p className="text-[11px] text-slate-400">of the week based on country</p>
//             </div>
//             <button className="text-xs text-slate-500 border border-slate-200 rounded-lg px-2 py-1 flex items-center gap-1 hover:bg-slate-50">
//               country <ChevronDown className="w-3 h-3" />
//             </button>
//           </div>
//           <div className="grid grid-cols-5 gap-3">
//             {/* map */}
//             <div className="col-span-3 relative rounded-xl overflow-hidden bg-blue-50" style={{ height: 160 }}>
//               <WorldMapSVG />
//               {/* US pin tooltip */}
//               <div className="absolute top-6 left-12 bg-blue-600 text-white text-[10px] rounded-lg px-2 py-1.5 shadow-lg">
//                 <p className="font-semibold">• United States</p>
//                 <p className="text-blue-200 text-[10px]">20,489</p>
//                 <p className="text-blue-300 text-[9px]">customers</p>
//               </div>
//               <div className="absolute bottom-3 right-3 flex gap-1.5">
//                 <button className="w-6 h-6 bg-white rounded-md shadow flex items-center justify-center hover:bg-slate-50">
//                   <ZoomIn className="w-3 h-3 text-slate-500" />
//                 </button>
//                 <button className="w-6 h-6 bg-white rounded-md shadow flex items-center justify-center hover:bg-slate-50">
//                   <ZoomOut className="w-3 h-3 text-slate-500" />
//                 </button>
//               </div>
//             </div>
//             {/* country list */}
//             <div className="col-span-2 space-y-3 flex flex-col justify-center">
//               {countryData.map((c) => (
//                 <div key={c.name}>
//                   <div className="flex items-center justify-between mb-0.5">
//                     <span className="text-[11px] text-slate-600 flex items-center gap-1">
//                       <span>{c.flag}</span> {c.name}
//                     </span>
//                     <span className="text-[11px] font-semibold text-slate-700">{c.pct}%</span>
//                   </div>
//                   <div className="w-full bg-slate-100 rounded-full h-1.5">
//                     <div
//                       className="h-1.5 rounded-full bg-blue-500"
//                       style={{ width: `${c.pct}%` }}
//                     />
//                   </div>
//                 </div>
//               ))}
//             </div>
//           </div>
//         </div>
//       </div>

//       {/* ── Bottom Row ── */}
//       <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
//         {/* Top Transactions */}
//         <div className="lg:col-span-1 bg-white rounded-2xl p-5 shadow-sm border border-slate-100">
//           <div className="flex items-center justify-between mb-1">
//             <div>
//               <h3 className="text-sm font-semibold text-slate-800">Top Transaction</h3>
//               <p className="text-[11px] text-slate-400">of the week based on total purchase</p>
//             </div>
//             <button className="text-xs text-blue-500 font-medium hover:underline">
//               View detail
//             </button>
//           </div>
//           <table className="w-full mt-3 text-xs">
//             <thead>
//               <tr className="text-slate-400 border-b border-slate-100">
//                 <th className="text-left pb-2 font-medium">Customer ID</th>
//                 <th className="text-left pb-2 font-medium">First Item</th>
//                 <th className="text-left pb-2 font-medium">Date</th>
//                 <th className="text-right pb-2 font-medium">Purchase</th>
//               </tr>
//             </thead>
//             <tbody>
//               {topTransactions.map((tx, i) => (
//                 <tr
//                   key={i}
//                   className={`border-b border-slate-50 last:border-0 ${
//                     tx.highlight ? "bg-blue-600 rounded-xl" : ""
//                   }`}
//                 >
//                   <td
//                     className={`py-2 font-medium ${
//                       tx.highlight ? "text-white" : "text-slate-700"
//                     }`}
//                   >
//                     {tx.id}
//                   </td>
//                   <td
//                     className={`py-2 max-w-[100px] truncate ${
//                       tx.highlight ? "text-blue-100" : "text-slate-600"
//                     }`}
//                   >
//                     {tx.item}
//                   </td>
//                   <td className={`py-2 ${tx.highlight ? "text-blue-200" : "text-slate-400"}`}>
//                     {tx.date}
//                   </td>
//                   <td
//                     className={`py-2 text-right font-semibold ${
//                       tx.highlight ? "text-white" : "text-slate-700"
//                     }`}
//                   >
//                     {tx.highlight ? (
//                       <span className="bg-white text-blue-600 rounded-full px-2 py-0.5 text-[10px]">
//                         See detail
//                       </span>
//                     ) : (
//                       tx.amount
//                     )}
//                   </td>
//                 </tr>
//               ))}
//             </tbody>
//           </table>
//         </div>

//         {/* Top Products */}
//         <div className="lg:col-span-1 bg-white rounded-2xl p-5 shadow-sm border border-slate-100">
//           <div className="flex items-center justify-between mb-3">
//             <div>
//               <h3 className="text-sm font-semibold text-slate-800">Top Product</h3>
//               <p className="text-[11px] text-slate-400">top 3 of the week based on total sold</p>
//             </div>
//             <button className="text-xs text-blue-500 font-medium hover:underline">
//               View more
//             </button>
//           </div>
//           <div className="grid grid-cols-3 gap-2 mb-3">
//             {[
//               { bg: "#f3f4f6", img: null, label: "Shirt" },
//               { bg: "#e0e7ff", img: null, label: "Dress" },
//               { bg: "#fef3c7", img: null, label: "Bag" },
//             ].map((p, i) => (
//               <div
//                 key={i}
//                 className="rounded-xl overflow-hidden aspect-square flex items-center justify-center text-3xl"
//                 style={{ background: p.bg }}
//               >
//                 {["👕", "👗", "👜"][i]}
//               </div>
//             ))}
//           </div>
//           <div className="border-t border-slate-100 pt-3">
//             <p className="text-sm font-semibold text-slate-800">Denim Jacket with White Feathers</p>
//             <p className="text-xs text-slate-400 mt-0.5">240+ items sold</p>
//           </div>
//         </div>

//         {/* CTA Card */}
//         <div
//           className="lg:col-span-1 rounded-2xl p-6 flex flex-col justify-between text-white relative overflow-hidden"
//           style={{
//             background: "linear-gradient(135deg, #1e3a5f 0%, #2563eb 60%, #7c3aed 100%)",
//           }}
//         >
//           {/* decorative circles */}
//           <div className="absolute -top-8 -right-8 w-36 h-36 rounded-full bg-white opacity-5" />
//           <div className="absolute bottom-4 -left-6 w-24 h-24 rounded-full bg-white opacity-5" />
//           <div className="absolute top-12 right-8 w-16 h-16 rounded-full bg-blue-400 opacity-20" />

//           <div>
//             <h3 className="text-lg font-bold leading-snug">
//               See more detail statistic to analyze your decision
//             </h3>
//           </div>
//           <button className="mt-6 self-start bg-white text-blue-700 text-sm font-semibold px-5 py-2 rounded-xl hover:bg-blue-50 transition shadow-md">
//             See more
//           </button>
//         </div>
//       </div>
//     </div>
//   );
// }


// working code 

// import { useEffect, useState } from "react";
// import {
//   AlertTriangle,
//   CheckCircle2,
//   FileText,
//   IndianRupee,
//   PercentCircleIcon,
//   ShieldCheck,
//   Store,
//   Users,
// } from "lucide-react";
// import { KpiCard } from "../../../Component/AdminComponents/KpiCard";
// import { SectionCard } from "../../../Component/AdminComponents/SectionCard";
// import { Badge } from "../../../Component/AdminComponents/Badge";
// import { formatCurrency } from "../../../Component/AdminComponents/formatCurrency";
// import { useNavigate } from "react-router-dom";
// import { ValueLoader } from "../../../Component/Common/ValueLoader.jsx";
// import { BASE_URL } from "../../../config/api.jsx";
// import axios from "axios";



// export default function OverView() {
//   const navigate = useNavigate();
//   const [loading, setLoading] = useState(true);

//   // NEW: State for dashboard API
//   const [dash, setDash] = useState({
//     category_count: 0,
//     brand_count: 0,
//     product_count: 0,
//     company_count: 0,
//   });

//   // NEW: Fetch API
//   useEffect(() => {
//     const fetchDashboard = async () => {
//       try {
//         const res = await fetch(
//           `${BASE_URL}warranty/warranty-dashboard`
//         );
//         const json = await res.json();
//         if (json.status && json.data) {
//           setDash(json.data);
//         }
//       } catch (err) {
//         console.error("Dashboard fetch error:", err);
//       } finally {
//         setLoading(false);
//       }
//     };
//     fetchDashboard();
//   }, []);

//   const [retailerLineStats, setRetailerLineStats] = useState(null);
//   const [lineLoading, setLineLoading] = useState(false);


//   const authUser = JSON.parse(localStorage.getItem("authUserData"));
// const locationMode = authUser?.location_mode;
// const userState = authUser?.state;
// const userDistrict = authUser?.district;

// const fetchRetailerLineChart = async () => {
//   try {
//     setLineLoading(true);

//     const params = {};
//     if (locationMode === "state") params.state = userState;
//     if (locationMode === "district") params.district = userDistrict;

//     const res = await axios.get(
//       `${BASE_URL}company/employee-retailer-line-chart`,
//       { params }
//     );

//     if (res.data?.success) {
//       setRetailerLineStats(res.data.data);
//     }
//   } catch (err) {
//     console.error("Line chart API failed", err);
//   } finally {
//     setLineLoading(false);
//   }
// };

// useEffect(() => {
//   fetchRetailerLineChart();
// }, []);

//   return (
//     <div className="space-y-4">
//       {/* KPI Grid */}
//       <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
//         <KpiCard
//           label="Category"
//           value={loading ? <ValueLoader /> : dash.category_count}
//           subLabel={`Active: ${dash.category_count}`} // keeping same UI
//           icon={Store}
//           onClick={() => navigate("/categories")}
//           className="cursor-pointer"
//         />

//         <KpiCard
//           label="Brand"
//           value={loading ? <ValueLoader /> : dash.brand_count}
//           subLabel="Using platform"
//           icon={CheckCircle2}
//           onClick={() => navigate("/brands")}
//         />

//         <KpiCard
//           label="Product"
//           value={loading ? <ValueLoader /> : dash.product_count}
//           subLabel="Field & onboarding"
//           icon={Users}
//           onClick={() => navigate("/products")}
//           className="cursor-pointer"
//         />

//         <KpiCard
//           label="MCP"
//           value={loading ? <ValueLoader /> : dash.company_count}
//           subLabel={`Total: ${dash.company_count}`}
//           icon={ShieldCheck}
//           onClick={() =>
//             navigate("/amdcomp", { state: { showOnlyActive: true } })
//           }
//         />

//         <KpiCard
//           label="Channel Partners (CP)"
//           value={loading ? <ValueLoader /> : dash.agent_count}
//           subLabel="Field & onboarding"
//           icon={Users}
//           onClick={() => navigate("/admagents")}
//           className="cursor-pointer"   // optional 
//         />

//         <KpiCard
//           label="ARP"
//           value={loading ? <ValueLoader /> : dash.retailer_count}
//           // subLabel={`Active: ${stats.activeRetailers}`}
//           icon={Store}
//           onClick={() => navigate("/admretailers")}
//           className="cursor-pointer"   // optional to show pointer UI
//         />

//         {/* <KpiCard
//           label="Employees"
//           // value={stats.totalRetailers}
//           // subLabel={`Active: ${stats.activeRetailers}`}
//           icon={Store}
//           onClick={() => navigate("/allemployees")}
//           className="cursor-pointer"   // optional to show pointer UI
//         /> */}

//         <KpiCard
//           label="Price Template"
//           value={loading ? <ValueLoader /> : dash.price_templates_count}
//           subLabel="All Products Price Template"
//           icon={PercentCircleIcon}
//           onClick={() =>
//             navigate("/pricetemplate")
//           }
//         />

//         <KpiCard
//           label="Connected"
//           value={loading ? <ValueLoader /> : dash.connected_retailers_count}
//           subLabel="Using platform"
//           icon={CheckCircle2}
//           onClick={() =>
//             navigate("/admretailers", { state: { filterStatus: "Connected" } })
//           }
//         />
//        <KpiCard
//           label="Active Warranties"
//           value={loading ? <ValueLoader /> : dash.active_warranties_count}
//           // subLabel={`Total: ${stats.totalWarranties}`}
//           icon={ShieldCheck}
//           onClick={() =>
//             navigate("/admwrnty", { state: { showOnlyActive: true } })
//           }
//         />

//         <KpiCard
//           label="Open Claims"
//           value={loading ? <ValueLoader /> : dash.open_claims_count}
//           // subLabel={`Settled: ${stats.settledClaims}`}
//           icon={FileText}
//           onClick={() =>
//             navigate("/admwrnty", { state: { showOnlyOpen: true } })
//           }
//         />
//         <KpiCard
//           label="Approved Commission"
//           // value={`₹ ${formatCurrency(stats.totalCommission)}`}
//           // subLabel={`Pending: ₹ ${formatCurrency(stats.pendingCommission)}`}
//           icon={IndianRupee}
//         />
//       </div>

//       {/* Summary Layout (UNCHANGED UI) */}
//       <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-2">
//         <SectionCard
//           title="Retailer Health Snapshot"
//           right={
//             <span className="hidden md:inline text-[11px] text-slate-400">
//               Based on connection & usage
//             </span>
//           }
//         >
//           <div className="text-xs space-y-3">
//             {lineLoading || !retailerLineStats ? (
//               <ValueLoader />
//             ) : (
//               <>
//                 <div className="flex justify-between items-center">
//                   <span className="text-slate-600">Connected retailers</span>
//                   <span className="font-semibold text-blue-700">
//                     {retailerLineStats.connected.count} /{" "}
//                     {retailerLineStats.connected.total}
//                   </span>
//                 </div>

//                 <div className="w-full bg-slate-100 rounded-full h-2 overflow-hidden">
//                   <div
//                     className="h-2 bg-blue-500 rounded-full"
//                     style={{
//                       width: `${retailerLineStats.connected.percent}%`,
//                     }}
//                   />
//                 </div>

//                 <p className="text-[11px] text-slate-500">
//                   Retailers who have successfully connected and are actively using the
//                   platform services.
//                 </p>
//               </>
//             )}
//           </div>
//         </SectionCard>


//         <SectionCard
//           title="Claims & Warranty Flow"
//           right={
//             <Badge color="yellow">
//               <AlertTriangle className="w-3 h-3 mr-1" />
//               Monitoring
//             </Badge>
//           }
//         >
//           <div className="text-xs space-y-2">
//             <p className="text-slate-600">
//               Combined view of retailer portal, retailer user portal and customer
//               portal flows:
//             </p>
//             <ul className="list-disc list-inside space-y-1 text-slate-600">
//               <li>
//                 Retailer sells warranty (from retailer dashboard), mapped to
//                 device IMEI & invoice.
//               </li>
//               <li>
//                 Customer or retailer raises claim (customer portal / retailer user
//                 portal).
//               </li>
//               <li>
//                 Ops & admin approve / reject with reasons (payment not paid, wrong
//                 product info / price, out of coverage etc.).
//               </li>
//               <li>
//                 Pickup / drop is arranged via channel partner, device goes to
//                 service centre and claim is settled.
//               </li>
//             </ul>
//           </div>
//         </SectionCard>

//         <SectionCard
//           title="Commission & Earnings"
//           right={
//             <Badge color="green">
//               <CheckCircle2 className="w-3 h-3 mr-1" />
//               Weekly cycle
//             </Badge>
//           }
//         >
//           <div className="text-xs space-y-2">
//             <p className="text-slate-600">
//               Commission generated from retailer warranty sales and onboarding by
//               agents is combined and settled weekly.
//             </p>
//             <ul className="list-disc list-inside text-slate-600 space-y-1">
//               <li>Agent-wise earnings (onboarding + performance).</li>
//               <li>Retailer wise earnings (per warranty & sales volume).</li>
//               <li>
//                 Transfer status with bank details, transaction ID & date.
//               </li>
//               <li>
//                 Downloadable weekly/monthly statements for audit.
//               </li>
//             </ul>
//           </div>
//         </SectionCard>
//       </div>
//     </div>
//   );
// }


// import { AlertTriangle, CheckCircle2, FileText, IndianRupee, ShieldCheck, Store, Users } from "lucide-react";
// import { KpiCard } from "../../../Component/AdminComponents/KpiCard";
// import { SectionCard } from "../../../Component/AdminComponents/SectionCard";
// import { Badge } from "../../../Component/AdminComponents/Badge";
// import { formatCurrency } from "../../../Component/AdminComponents/formatCurrency";
// import { useNavigate } from "react-router-dom";

// export default function OverView({ stats }) {
//   const navigate = useNavigate();
  
//   return (
//     <div className="space-y-4">
//       {/* KPI Grid */}
//       <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
//         <KpiCard
//           label="Category"
//           value={stats.totalRetailers}
//           subLabel={`Active: ${stats.activeRetailers}`}
//           icon={Store}
//           onClick={() => navigate("/categories")}
//           className="cursor-pointer"   // optional to show pointer UI
//         />

//         <KpiCard
//           label="Brand"
//           value={stats.connectedRetailers}
//           subLabel="Using platform"
//           icon={CheckCircle2}
//           onClick={() => navigate("/brands")}
//         />

//         <KpiCard
//           label="Product"
//           value={stats.totalAgents}
//           subLabel="Field & onboarding"
//           icon={Users}
//           onClick={() => navigate("/admagents")}
//           className="cursor-pointer"   // optional 
//         />
//        <KpiCard
//           label="Companies"
//           value={stats.activeWarranties}
//           subLabel={`Total: ${stats.totalWarranties}`}
//           icon={ShieldCheck}
//           onClick={() =>
//             navigate("/admwrnty", { state: { showOnlyActive: true } })
//           }
//         />
//         {/* <KpiCard
//           label="Open Claims"
//           value={stats.openClaims}
//           subLabel={`Settled: ${stats.settledClaims}`}
//           icon={FileText}
//           onClick={() =>
//             navigate("/admwrnty", { state: { showOnlyOpen: true } })
//           }
//         />
//         <KpiCard
//           label="Approved Commission"
//           value={`₹ ${formatCurrency(stats.totalCommission)}`}
//           subLabel={`Pending: ₹ ${formatCurrency(stats.pendingCommission)}`}
//           icon={IndianRupee}
//         /> */}
//       </div>

//       {/* Summary Layout */}
//       <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-2">
//         <SectionCard
//           title="Retailer Health Snapshot"
//           right={
//             <span className="hidden md:inline text-[11px] text-slate-400">
//               Based on connection & usage
//             </span>
//           }
//         >
//           <div className="text-xs space-y-3">
//             <div className="flex justify-between items-center">
//               <span className="text-slate-600">Connected retailers</span>
//               <span className="font-semibold text-blue-700">
//                 {stats.connectedRetailers} / {stats.totalRetailers}
//               </span>
//             </div>
//             <div className="w-full bg-slate-100 rounded-full h-2 overflow-hidden">
//               <div
//                 className="h-2 bg-blue-500 rounded-full"
//                 style={{
//                   width: `${
//                     stats.totalRetailers
//                       ? (stats.connectedRetailers / stats.totalRetailers) * 100
//                       : 0
//                   }%`,
//                 }}
//               />
//             </div>
//             <p className="text-[11px] text-slate-500">
//               Track which retailers are activated, connected to APIs, using the app
//               for sales, warranties, and claims.
//             </p>
//           </div>
//         </SectionCard>

//         <SectionCard
//           title="Claims & Warranty Flow"
//           right={
//             <Badge color="yellow">
//               <AlertTriangle className="w-3 h-3 mr-1" />
//               Monitoring
//             </Badge>
//           }
//         >
//           <div className="text-xs space-y-2">
//             <p className="text-slate-600">
//               Combined view of retailer portal, retailer user portal and customer
//               portal flows:
//             </p>
//             <ul className="list-disc list-inside space-y-1 text-slate-600">
//               <li>
//                 Retailer sells warranty (from retailer dashboard), mapped to device
//                 IMEI & invoice.
//               </li>
//               <li>
//                 Customer or retailer raises claim (customer portal / retailer user
//                 portal).
//               </li>
//               <li>
//                 Ops & admin approve / reject with reasons (payment not paid, wrong
//                 product info / price, out of coverage etc.).
//               </li>
//               <li>
//                 Pickup / drop is arranged via channel partner, device goes to
//                 service centre and claim is settled.
//               </li>
//             </ul>
//           </div>
//         </SectionCard>

//         <SectionCard
//           title="Commission & Earnings"
//           right={
//             <Badge color="green">
//               <CheckCircle2 className="w-3 h-3 mr-1" />
//               Weekly cycle
//             </Badge>
//           }
//         >
//           <div className="text-xs space-y-2">
//             <p className="text-slate-600">
//               Commission generated from retailer warranty sales and onboarding by
//               agents is combined and settled weekly.
//             </p>
//             <ul className="list-disc list-inside text-slate-600 space-y-1">
//               <li>Agent-wise earnings (onboarding + performance).</li>
//               <li>Retailer wise earnings (per warranty & sales volume).</li>
//               <li>Transfer status with bank details, transaction ID & date.</li>
//               <li>Downloadable weekly/monthly statements for audit.</li>
//             </ul>
//           </div>
//         </SectionCard>
//       </div>
//     </div>
//   );
// }